// 剣舞の「暗器投げ」(開発版 ?bladeThrow=1・技の中身は src/blade_dancer_throw.ts) の小刀を描く。
//   render.ts から呼ぶので render.ts を読み込まない (循環を作らない)。技が入っていない時は何も描かない。
//   小刀は放った高さのまま、刃先を前へ向けて一直線に飛ぶ (10-27 作者「ペンライトを投げているみたい・鋭く一直線に」。
//   手本 = レラーナの宙に浮かべた青い剣を撃ち出す技。取った特徴は「回らない・刃先が進む向き・速い・光って現れる」だけ)。
//   当たりは横の位置だけ (飛ぶ弾の決まり・hits.ts)。白い線・光の筋は描かない。残像は刃の形のまま、尾は銀と藤の粒。
import { phaseProgressOf } from './boss_pose_timeline'
import { KNIFE_IMG, bladeFxRestImg, bladeKnifePaintOn } from './blade_dancer_fx_rest'
import type { ActiveHit, Boss } from './types'

const BOSS_ID = 'blade-dancer'
export const BLADE_THROW_ID = 'bladeKnifeThrow'

// 手 (画面 px・足元から前, 上)。構え c3 の引いた拳・放つ c2 の差し伸べた手 (src/blade_dancer_pull.ts と同じ絵)。
export const THROW_HAND_DRAW = { fwd: 43, up: 96 }
export const THROW_HAND_RELEASE = { fwd: 124, up: 118 }
/** 構えの手の所と小刀の向き (右向きの時の rad)。アルレ版は振りかぶりの絵が違うので差し替える (src/blade_dancer_waltz.ts)。 */
export type ThrowHandGrip = { fwd: number; up: number; rad: number }
const DEFAULT_GRIP: ThrowHandGrip = { ...THROW_HAND_DRAW, rad: Math.PI * 0.82 }
let grip: ThrowHandGrip = DEFAULT_GRIP
export function setBladeThrowHandGrip(g: ThrowHandGrip | null): void { grip = g ?? DEFAULT_GRIP }
export function bladeThrowHandGrip(): ThrowHandGrip { return grip }
/** 構えのこの所から手に小刀が見える (溜めの割合)。 */
export const KNIFE_SHOW_AT = 0.15

// 刃の先から柄頭まで (px)。手に持つ剣と同じ長さ (10-25 作者「手に持ってるサイズと同じサイズの刀を投げて良い」)。
//   84 = 立ち絵の剣の長さ ÷ 立ち絵の高さ (0.45) × 画面の背丈 186px (撮影 arle-gamma/f0030 で実測)。
export const KNIFE_LEN = 84
const FLASH_MS = 90             // 放った所で光る長さ
const BLADE_HI = '#f4f2fb', BLADE_MID = '#c9c6dc', BLADE_LO = '#8d88a8'
const GRIP = '#6b4a9c', GUARD = '#d8c7f2'

type Pt = { x: number; y: number }

function hash01(n: number): number { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }

/** 剣1本 (中心 c・向き rad・明るさ a)。刃は +x 向き。立ち絵の剣と同じ形 = 真っすぐな細身の刃・横に張った鍔・短い柄。 */
function knife(ctx: CanvasRenderingContext2D, c: Pt, rad: number, a: number): void {
  ctx.save()
  ctx.translate(c.x, c.y)
  ctx.rotate(rad)
  ctx.globalAlpha *= a
  const h = KNIFE_LEN / 2
  // 描いた絵 (11-01 既定・?bladeFxRest=sash で前の描き方)：立ち絵の剣と同じ剣を描いた絵1枚 (柄頭が左・刃先が右)。
  const img = bladeKnifePaintOn() ? bladeFxRestImg('blade-dancer/knife') : null
  if (img) {
    const ih = KNIFE_LEN * KNIFE_IMG.h / KNIFE_IMG.w
    ctx.drawImage(img, -h, -ih / 2, KNIFE_LEN, ih)
    ctx.restore()
    return
  }
  const guardX = -h + KNIFE_LEN * 0.2   // 柄は全長の2割
  // 刃 (幅 4.6px の真っすぐな刃・先だけ尖る)
  const g = ctx.createLinearGradient(0, -2.3, 0, 2.3)
  g.addColorStop(0, BLADE_HI); g.addColorStop(0.55, BLADE_MID); g.addColorStop(1, BLADE_LO)
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.moveTo(h, 0)
  ctx.lineTo(h - 7, -2.3)
  ctx.lineTo(guardX, -2.3)
  ctx.lineTo(guardX, 2.3)
  ctx.lineTo(h - 7, 2.3)
  ctx.closePath()
  ctx.fill()
  // 鍔と柄と柄頭
  ctx.fillStyle = GUARD
  ctx.fillRect(guardX - 2.6, -6.5, 2.6, 13)
  ctx.fillStyle = GRIP
  ctx.fillRect(-h + 2, -1.9, guardX - 2.6 + h - 2, 3.8)
  ctx.fillStyle = GUARD
  ctx.beginPath(); ctx.arc(-h + 2, 0, 2.6, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}

/** 刃の形のまま光らせる (同じ小刀の絵を加算で重ねる)。丸い光を使わない。 */
function knifeGlint(ctx: CanvasRenderingContext2D, c: Pt, rad: number, a: number): void {
  if (a <= 0) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  knife(ctx, c, rad, a)
  ctx.restore()
}

function mote(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, a: number): void {
  ctx.save()
  ctx.globalAlpha *= a
  ctx.globalCompositeOperation = 'lighter'
  // 描いた絵 (11-01 既定・?bladeFxRest=sash で前の描き方)：丸いぼかしを、剣舞の花びらの描いた光に替える。白っぽい粒は glint、藤は glow。
  if (bladeKnifePaintOn()) {
    const white = color.startsWith('#f') || color.startsWith('rgba(24') || color.startsWith('rgba(23')
    const img = bladeFxRestImg(white && r < 8 ? 'blade-petals/glint_1' : white ? 'blade-petals/glow_1' : 'blade-petals/glow_2')
    if (img) {
      const d = r * (r < 8 ? 2.6 : 2)
      ctx.drawImage(img, x - d / 2, y - d / 2, d, d)
      ctx.restore()
      return
    }
  }
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, color); g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
  ctx.restore()
}

/** 飛んでいる小刀の見た目の位置と向き (テストからも呼ぶ)。刃先を進む向きへ向け、放った高さのまま真っすぐ飛ぶ。回らない。 */
export function bladeKnifePose(hit: Pick<ActiveHit, 'x' | 'y' | 'vx' | 'elapsedMs' | 'reflected'>): { c: Pt; rad: number } {
  const dir = (hit.vx ?? 1) >= 0 ? 1 : -1
  return { c: { x: hit.x ?? 0, y: hit.y ?? 0 }, rad: dir > 0 ? 0 : Math.PI }
}

/** 飛んでいる小刀 (drawCastProjectiles から1本ずつ)。 */
export function drawBladeKnife(ctx: CanvasRenderingContext2D, hit: ActiveHit): void {
  const { c, rad } = bladeKnifePose(hit)
  const dir = (hit.vx ?? 1) >= 0 ? 1 : -1
  // 残像: 同じ向きの刃を後ろへ薄く4枚 (線は引かない・刃の形のまま残す)。
  for (let i = 4; i >= 1; i--) {
    knife(ctx, { x: c.x - dir * KNIFE_LEN * 0.42 * i, y: c.y }, rad, 0.3 / (i + 0.6))
  }
  // 刃を包む藤の光 (刃に沿って細長く)。描いた絵 (11-01 既定・?bladeFxRest=sash で前の描き方) は丸い光の絵を潰すと白い楕円が刃の後ろに残って見えるので、刃の形のまま光らせる (11-01)。
  if (bladeKnifePaintOn()) {
    knife(ctx, c, rad, 1)
    knifeGlint(ctx, c, rad, 0.35)
  } else {
    ctx.save()
    ctx.translate(c.x, c.y)
    ctx.scale(2.6, 0.55)
    mote(ctx, 0, 0, KNIFE_LEN * 0.32, 'rgba(200,180,255,0.75)', 0.8)
    ctx.restore()
    knife(ctx, c, rad, 1)
  }
  // 放った瞬間の光 (宙に刃が現れて撃ち出される)。
  //   描いた絵 (11-01 既定・?bladeFxRest=sash で前の描き方) は丸い光の絵が白い丸に見えるので、刃そのものを光らせる (11-01 作者「白い丸いのが…残ってる感じのけしてほしい」)。
  if (hit.elapsedMs < FLASH_MS) {
    if (bladeKnifePaintOn()) knifeGlint(ctx, c, rad, 1 - hit.elapsedMs / FLASH_MS)
    else mote(ctx, c.x, c.y, 34, 'rgba(240,232,255,0.95)', 1 - hit.elapsedMs / FLASH_MS)
  }
  // 尾: 後ろへ置いていく銀と藤の粒 (その場に残って消える)。
  const seed = hit.id * 7.7 + Math.floor(hit.elapsedMs / 30)
  for (let i = 0; i < 5; i++) {
    const r = (k: number) => hash01(seed * 3.1 + i * 5.3 + k)
    const age = r(1)
    const px = c.x - dir * (KNIFE_LEN * 0.5 + age * 110)
    const py = c.y + (r(2) - 0.5) * (4 + age * 10)
    mote(ctx, px, py, 1.8 + r(4) * 2.2, r(5) > 0.5 ? '#f2eeff' : '#b79cf0', (1 - age) * 0.85)
  }
}

/** アルレ版の締めで、空いた手に剣が戻る所 (recovery の割合・src/blade_dancer_waltz.ts)。 */
export const THROW_REARM_AT = 0.36
const REARM_FLASH = 0.12

/** 構えの間、手に握った小刀 (投げる前の合図)。アルレ版の締めでは、手に剣が戻る所を光らせる。 */
export function drawBladeThrowHand(ctx: CanvasRenderingContext2D, boss: Boss, footY: number): void {
  if (boss.id !== BOSS_ID || boss.currentAttack?.id !== BLADE_THROW_ID) return
  const { phase, progress } = phaseProgressOf(boss)
  if (phase === 'recovery' && grip !== DEFAULT_GRIP) {
    const k = (progress - THROW_REARM_AT) / REARM_FLASH
    if (k < 0 || k > 1) return
    const dir = boss.facing === 'right' ? 1 : -1
    // 描いた絵 (11-01 既定・?bladeFxRest=sash で前の描き方) は丸い光でなく、小さなきらめき1つ (白い丸を残さない・11-01)。
    if (bladeKnifePaintOn()) mote(ctx, boss.x + dir * (THROW_HAND_RELEASE.fwd - 8), footY - THROW_HAND_RELEASE.up, 7, '#f2eeff', 1 - k)
    else mote(ctx, boss.x + dir * (THROW_HAND_RELEASE.fwd - 8), footY - THROW_HAND_RELEASE.up, 30, 'rgba(240,232,255,0.95)', 1 - k)
    return
  }
  const holding = (phase === 'telegraph' && progress >= KNIFE_SHOW_AT) || (phase === 'comboGap' && progress >= 0.3)
  if (!holding) return
  const dir = boss.facing === 'right' ? 1 : -1
  const c = { x: boss.x + dir * grip.fwd, y: footY - grip.up }
  // 刃を後ろ下へ向けて握る。投げる直前ほど光る (光り始め = 投げる合図)。
  const glint = phase === 'telegraph' ? Math.max(0, (progress - 0.6) / 0.4) : Math.max(0, (progress - 0.6) / 0.4)
  // 握る所は刃の向きへ 10.5px ずれた所 (既定の向きで前の (−9, +5) と同じ)。
  const rad = dir > 0 ? grip.rad : Math.PI - grip.rad
  const k = { x: c.x + Math.cos(rad) * 10.5, y: c.y + Math.sin(rad) * 10.5 }
  // 描いた絵 (11-01 既定・?bladeFxRest=sash で前の描き方) は手に丸い光を置かず、刃を光らせて合図にする (手の白い丸が投げの間も残って見えた・11-01 作者の指摘)。
  if (bladeKnifePaintOn()) {
    knife(ctx, k, rad, 1)
    knifeGlint(ctx, k, rad, glint * 0.85)
    return
  }
  mote(ctx, k.x - dir, k.y + 1, 22, 'rgba(235,225,255,0.95)', 0.4 + glint * 0.6)
  knife(ctx, k, rad, 1)
}
